import React, { useState, useEffect, useCallback } from "react";
import { TbCurrentLocation } from "react-icons/tb";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import LocationPicker from "../LocationPicker/LocationPicker";

const CustomerAddress = () => {
    const getAddress = parseInt(localStorage.getItem("getAddress"), 10) || 0;
    const NewAddressId = parseInt(localStorage.getItem("totalAddresses") || "0", 10);

    const navigate = useNavigate();
    const API_BASE_URL = process.env.REACT_APP_API_KEY;

    // Get customerData from localStorage
    const storedCustomerData = JSON.parse(localStorage.getItem("customerData")) || {};

    const [customerMaster, setCustomerMaster] = useState({
        customerId: storedCustomerData?.customerId || 0,
        addresses: storedCustomerData?.addresses || []
    });

    // Get the correct addressId based on add or update
    const currentAddressId = getAddress === 0 ? NewAddressId + 1 : parseInt(localStorage.getItem("addId"), 10);

    // Get current address object from state
    const currentAddress = customerMaster.addresses.find(addr => addr.addressId === currentAddressId);

    const pgHeader = getAddress === 0 ? "Add Address" : "Update Address";

    const [latitude, setLatitude] = useState(currentAddress?.latitude || 0);
    const [longitude, setLongitude] = useState(currentAddress?.longitude || 0);
    const [showMap, setShowMap] = useState(false);
    const [formErrors, setFormErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    // One-time add blank address if adding
    useEffect(() => {
        if (getAddress === 0) {
            setCustomerMaster(prev => {
                const exists = prev.addresses.some(addr => addr.addressId === currentAddressId);
                if (exists) return prev;

                const newAddress = {
                    addressId: currentAddressId,
                    area: "",
                    addLine1: "",
                    addLine2: "",
                    nearBy: "",
                    city: "",
                    state: "",
                    pincode: "",
                    latitude: 0,
                    longitude: 0
                };

                return {
                    ...prev,
                    addresses: [...prev.addresses, newAddress]
                };
            });
        }
    }, [getAddress, currentAddressId]);

    const storedData = JSON.parse(localStorage.getItem("outletdataLocal")) || {};
    const { outletId, outletlogo, outletName } = Array.isArray(storedData) ? storedData[0] || {} : storedData;

    const getCurrentLocation = useCallback(() => {
        if (!navigator.geolocation) {
            alert("Geolocation not supported.");
            return;
        }

        navigator.geolocation.getCurrentPosition(
            (position) => {
                const { latitude, longitude } = position.coords;
                setLatitude(latitude);
                setLongitude(longitude);
                fetchAddressFromCoordinates(latitude, longitude);
            },
            (error) => {
                alert("Error getting location.");
            },
            { enableHighAccuracy: true }
        );
    }, []);

    const fetchAddressFromCoordinates = useCallback(async (lat, lon) => {
        try {
            const response = await axios.get("https://nominatim.openstreetmap.org/reverse", {
                params: { lat, lon, format: "json" }
            });

            const address = response.data.address || {};

            const updatedAddress = {
                city: address.town || address.village || address.city || "",
                state: address.state || "",
                pincode: address.postcode || "",
                latitude: lat,
                longitude: lon
            };

            setCustomerMaster(prev => {
                const updatedAddresses = [...prev.addresses];
                const index = updatedAddresses.findIndex(addr => addr.addressId === currentAddressId);
                if (index === -1) return prev;

                updatedAddresses[index] = {
                    ...updatedAddresses[index],
                    ...updatedAddress
                };
                return { ...prev, addresses: updatedAddresses };
            });
        } catch (error) {
            console.error("Error fetching address from coordinates");
        }
    }, [currentAddressId]);

    const handleAddressChange = (e) => {
        const { name, value } = e.target;

        setCustomerMaster(prev => {
            const updatedAddresses = [...prev.addresses];
            const index = updatedAddresses.findIndex(addr => addr.addressId === currentAddressId);
            if (index === -1) return prev;

            updatedAddresses[index] = {
                ...updatedAddresses[index],
                [name]: value,
                latitude: latitude || updatedAddresses[index].latitude,
                longitude: longitude || updatedAddresses[index].longitude
            };

            return {
                ...prev,
                addresses: updatedAddresses
            };
        });

        if (formErrors[name]) {
            setFormErrors(prev => ({ ...prev, [name]: "" }));
        }
    };

    const validateForm = () => {
        const address = customerMaster.addresses.find(addr => addr.addressId === currentAddressId);

        const errors = {
            area: !address?.area ? "Area is required" : "",
            addLine1: !address?.addLine1 ? "Address Line 1 is required" : "",
            nearBy: !address?.nearBy ? "Nearby landmark is required" : "",
            city: !address?.city ? "City is required" : "",
            coordinates: !latitude || !longitude ? "Set location coordinates" : ""
        };

        setFormErrors(errors);
        return !Object.values(errors).some(err => err);
    };

    const RegisterCustomer = async () => {
        if (!validateForm()) return;

        setIsSubmitting(true);

        const address = customerMaster.addresses.find(addr => addr.addressId === currentAddressId);
        if (!address) {
            alert("Address not found.");
            setIsSubmitting(false);
            return;
        }

        const payload = {
            customerId: customerMaster.customerId,
            addresses: [{
                ...address,
                latitude,
                longitude
            }]
        };

        try {
            const response = await axios.post(`${API_BASE_URL}CustomerMaster/UpsertCustomerAddress`, payload);
            const addressId = response?.data?.addressId;

            if (addressId !== undefined && addressId !== null) {
                
                localStorage.setItem("selectedAddressId", addressId);
                localStorage.setItem("UpdateAndAddAddressId", addressId);
                localStorage.setItem("addId", addressId);
                localStorage.setItem("addressIndex", addressId);
            }

            localStorage.removeItem("getAddress");
            navigate("/QrCartItems");
        } catch (error) {
            console.error("Submission error", error);
            alert("Failed to save address.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const addressFields = [
        { name: "area", label: "Area", required: true },
        { name: "addLine1", label: "Address Line 1", required: true },
        { name: "addLine2", label: "Address Line 2", required: false },
        { name: "nearBy", label: "Nearby Landmark", required: true },
        { name: "city", label: "City", required: true },
        { name: "state", label: "State", required: false },
        { name: "pincode", label: "Pincode", required: false }
    ];

    return (
        <div className="container-fluid">
            <div className="row">
                <nav className="navbar navbar-light bg-light shadow-sm bg-body rounded">
                    <div className="container-fluid">
                        <a className="navbar-brand" href="#">
                            <img src={outletlogo} alt="Outlet Logo" width={60} height={40} className="me-2" />
                            <strong>{outletName}</strong>
                        </a>
                    </div>
                </nav>

                <div className="col-12 mt-2 mb-2 text-center">
                    <h3>{pgHeader}</h3>
                </div>

                <div className="col-md-12 border p-3">
                    <div className="mb-3">
                        <label className="form-label">Location</label>
                        <div className="d-flex align-items-center gap-2">
                            <input
                                type="text"
                                style={{ width: "50%" }}
                                className={`form-control ${formErrors.coordinates ? "is-invalid" : ""}`}
                                value={latitude && longitude ? `${latitude.toFixed(6)}, ${longitude.toFixed(6)}` : ""}
                                readOnly
                                placeholder="Current location coordinates"
                            />
                            <button
                                className="btn btn-outline-primary"
                                onClick={getCurrentLocation}
                                disabled={isSubmitting}
                            >
                                <TbCurrentLocation size={24} />
                            </button>
                            <button
                                className="btn btn-primary"
                                onClick={() => setShowMap(!showMap)}
                                disabled={!latitude}
                            >
                                {showMap ? "Close Map" : "Open Map"}
                            </button>
                        </div>
                        {formErrors.coordinates && <div className="invalid-feedback d-block">{formErrors.coordinates}</div>}
                    </div>

                    {showMap && (
                        <div className="mb-3">
                            <LocationPicker
                                latitude={latitude}
                                longitude={longitude}
                                setLatitude={setLatitude}
                                setLongitude={setLongitude}
                                fetchAddressFromCoordinates={fetchAddressFromCoordinates}
                            />
                        </div>
                    )}

                    <div className="row">
                        {addressFields.map((field) => (
                            <div key={field.name} className="col-6 mb-3">
                                <label className="form-label">{field.label}</label>
                                <input
                                    type="text"
                                    className={`form-control ${formErrors[field.name] ? "is-invalid" : ""}`}
                                    name={field.name}
                                    value={
                                        customerMaster.addresses.find(addr => addr.addressId === currentAddressId)?.[field.name] || ""
                                    }
                                    onChange={handleAddressChange}
                                    disabled={isSubmitting}
                                />
                                {formErrors[field.name] && (
                                    <div className="invalid-feedback">{formErrors[field.name]}</div>
                                )}
                            </div>
                        ))}
                    </div>

                    <div className="d-flex justify-content-center mt-4">
                        <button
                            className="btn btn-primary px-4"
                            onClick={RegisterCustomer}
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? "Submitting..." : "Submit"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CustomerAddress;
